import { useCallback, useMemo, useRef, useState } from "react";
import { taskService } from "@miplatform/api";
import {
  HTTP_STATUSES,
  LABEL_CONFIG_AREAS,
  LABEL_CONFIG_DEPENDENT_TYPES,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  ORDER_FIELDS_CONFIG_TYPE,
  getMaxDisplayOrder,
} from "./manageOrderCardFieldsConstants";

export const useManageOrderCardFieldsViewModel = () => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const configRef = useRef({
    [ORDER_FIELDS_CONFIG_TYPE.ORDER_CARD]: null,
    [ORDER_FIELDS_CONFIG_TYPE.ORDER_HOVER_CARD]: null,
  });
  const [orderCardConfig, setOrderCardConfig] = useState(null);
  const [orderHoverCardConfig, setOrderHoverCardConfig] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const { orderMaxDisplayOrder, hoverMaxDisplayOrder } = useMemo(
    () => ({
      hoverMaxDisplayOrder: getMaxDisplayOrder(orderHoverCardConfig),
      orderMaxDisplayOrder: getMaxDisplayOrder(orderCardConfig),
    }),
    [orderCardConfig, orderHoverCardConfig]
  );

  const getConfig = ({
    dependentType = LABEL_CONFIG_DEPENDENT_TYPES.ORDERS,
    area,
  }) =>
    stateManager.run(async () => {
      const response = await taskService.labelConfig.get({
        dependentType,
        area,
      });
      if (response.status === HTTP_STATUSES.OK) {
        if (area === LABEL_CONFIG_AREAS.CARD) {
          setOrderCardConfig(response.data);
          configRef.current = {
            ...configRef.current,
            [ORDER_FIELDS_CONFIG_TYPE.ORDER_CARD]: response.data,
          };
        } else {
          setOrderHoverCardConfig(response.data);
          configRef.current = {
            ...configRef.current,
            [ORDER_FIELDS_CONFIG_TYPE.ORDER_HOVER_CARD]: response.data,
          };
        }
      }
    });

  const handleFieldChange = useCallback(
    (fieldKey, isSelected, currentDisplayOrder, configType) => {
      const maxDisplayOrder =
        configType === ORDER_FIELDS_CONFIG_TYPE.ORDER_CARD
          ? orderMaxDisplayOrder
          : hoverMaxDisplayOrder;

      setIsDirty(true);

      (configType === ORDER_FIELDS_CONFIG_TYPE.ORDER_CARD
        ? setOrderCardConfig
        : setOrderHoverCardConfig)((prevConfig) => {
        return prevConfig.map((field) => {
          if (field.fieldKey === fieldKey) {
            return {
              ...field,
              isSelected,
              displayOrder: isSelected ? maxDisplayOrder + 1 : null,
            };
          }

          if (
            !isSelected &&
            field.displayOrder &&
            field.displayOrder > currentDisplayOrder
          ) {
            return {
              ...field,
              displayOrder: field.displayOrder - 1,
            };
          }

          return field;
        });
      });
    },
    [orderMaxDisplayOrder, hoverMaxDisplayOrder]
  );

  const handleDisplayOrderChange = useCallback(
    (fieldKey, currentDisplayOrder, newDisplayOrder, configType) => {
      setIsDirty(true);
      (configType === ORDER_FIELDS_CONFIG_TYPE.ORDER_CARD
        ? setOrderCardConfig
        : setOrderHoverCardConfig)((prevConfig) =>
        prevConfig.map((field) => {
          if (field.fieldKey === fieldKey) {
            return {
              ...field,
              displayOrder: newDisplayOrder,
            };
          }

          if (field.displayOrder === newDisplayOrder) {
            return {
              ...field,
              displayOrder: currentDisplayOrder,
            };
          }

          return field;
        })
      );
    },
    []
  );

  const updateConfig = ({
    dependentType = LABEL_CONFIG_DEPENDENT_TYPES.ORDERS,
    area,
    data,
  }) =>
    stateManager.run(async () => {
      const response = taskService.labelConfig.update({
        dependentType,
        area,
        data,
        ...(area === LABEL_CONFIG_AREAS.CARD && {
          toastMessageConfig: {
            success: {
              text: "Card fields config updated successfully",
            },
          },
        }),
      });
      if (response.status === HTTP_STATUSES.OK) {
        if (area === LABEL_CONFIG_AREAS.CARD) {
          configRef.current = {
            ...configRef.current,
            [ORDER_FIELDS_CONFIG_TYPE.ORDER_CARD]: orderCardConfig,
          };
        } else {
          configRef.current = {
            ...configRef.current,
            [ORDER_FIELDS_CONFIG_TYPE.ORDER_HOVER_CARD]: orderHoverCardConfig,
          };
        }
      }
    });

  const handleReset = () => {
    setOrderCardConfig(configRef.current[ORDER_FIELDS_CONFIG_TYPE.ORDER_CARD]);
    setOrderHoverCardConfig(
      configRef.current[ORDER_FIELDS_CONFIG_TYPE.ORDER_HOVER_CARD]
    );
    setIsDirty(false);
  };

  const handleSubmit = async () => {
    Promise.allSettled([
      updateConfig({
        area: LABEL_CONFIG_AREAS.CARD,
        data: orderCardConfig?.filter((field) => field.isSelected),
      }),
      updateConfig({
        area: LABEL_CONFIG_AREAS.HOVER_CARD,
        data: orderHoverCardConfig?.filter((field) => field.isSelected),
      }),
    ]).then(() => setIsDirty(false));
  };

  return {
    isLoading,
    isDirty,
    orderCardConfig,
    orderHoverCardConfig,
    orderMaxDisplayOrder,
    hoverMaxDisplayOrder,
    getConfig,
    handleFieldChange,
    handleDisplayOrderChange,
    handleReset,
    handleSubmit,
  };
};
